import * as React from "react";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, FolderX, Loader2 } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { dialogWide } from "@/lib/dialogSize";

import TableEmptyState from "../common/TableEmptyState";
import { QUERY_KEYS } from "../../constants/QueryKeys";
import { convertUtcToLocal } from "../../helpers/CommonHelpers";
import { SourceRootOutcome, isUnreachable } from "../../models/ConfiguredSourceRoot";
import { DiscoveryCycle } from "../../models/Statistics/DiscoveryCycle";
import { SourceLinksRepository } from "../../repositories/SourceLinksRepository";
import ConfiguredSourceRootsTable from "../SourceLinks/ConfiguredSourceRootsTable";

const numberFormat = new Intl.NumberFormat();

/**
 * Which roots to show. The registry holds one row per root and every report overwrites it, so a cycle's own
 * roots and the outages standing now are genuinely different questions: an outage the client has since read
 * successfully leaves the first list without leaving the second, and a root broken for weeks appears in both.
 */
type RootScope = "cycle" | "standing";

const ScopeLabels: Record<RootScope, string> = {
  cycle: "This cycle",
  standing: "Unreachable now",
};

/**
 * The roots behind a cycle's unreachable-roots figure. That figure is a tally the client sends when it ends a
 * cycle and names nothing; these rows are the only place a root is identified.
 */
const ConfiguredSourceRootsModal: React.FC<{
  cycle: DiscoveryCycle;
  organizationId: string;
  open: boolean;
  onClose: () => void;
}> = ({ cycle, organizationId, open, onClose }) => {
  const [scope, setScope] = useState<RootScope>("cycle");
  const [fellBack, setFellBack] = useState(false);
  const sourceLinksRepository = new SourceLinksRepository();

  const rootsQuery = useQuery({
    queryKey: [QUERY_KEYS.CONFIGURED_SOURCE_ROOTS, organizationId, cycle.id, scope],
    enabled: open,
    queryFn: () =>
      sourceLinksRepository.getConfiguredSourceRoots(
        scope === "cycle"
          ? { discoveryCycleId: cycle.id }
          : { outcome: SourceRootOutcome.Unreachable }
      ),
  });

  const roots = rootsQuery.data ?? [];
  const unreachable = roots.filter(isUnreachable);

  // A cycle names its own roots from the append-only observations, which only exist for cycles run since the
  // client started recording them. An older cycle therefore answers empty however unreachable its roots were,
  // and an empty list reads as "nothing was wrong" rather than "this cannot be attributed". Falling through to
  // the standing outages puts the answer on screen instead of one click away.
  //
  // Fires at most once per opening. The effect's own setScope feeds back into its dependencies, so without the
  // latch a manual return to the cycle scope re-runs it and snaps straight back, leaving that scope unreachable.
  const fallbackApplied = React.useRef(false);

  React.useEffect(() => {
    if (!open) {
      fallbackApplied.current = false;
      return;
    }

    if (fallbackApplied.current || scope !== "cycle" || !rootsQuery.isSuccess) return;
    if ((rootsQuery.data?.length ?? 0) > 0 || cycle.rootsUnreachable <= 0) return;

    fallbackApplied.current = true;
    setScope("standing");
    setFellBack(true);
  }, [open, scope, rootsQuery.isSuccess, rootsQuery.data, cycle.rootsUnreachable]);

  // Only in cycle scope: the standing list is not a claim about this cycle, so its size says nothing about
  // whether the cycle's tally was honoured. Signed, like the census gaps — a client that tallied roots it never
  // reported and one that reported more than it tallied are different faults.
  const attributionGap =
    scope === "cycle" && rootsQuery.isSuccess ? cycle.rootsUnreachable - unreachable.length : undefined;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          setScope("cycle");
          setFellBack(false);
          onClose();
        }
      }}
    >
      <DialogContent className={`${dialogWide} flex flex-col overflow-hidden`}>
        <DialogHeader>
          <DialogTitle>Configured source roots</DialogTitle>
          <DialogDescription>
            The folders this discovery reads its records from. Client {cycle.clientId}, cycle started{" "}
            {convertUtcToLocal(cycle.startDate)}.
          </DialogDescription>
        </DialogHeader>

        <Separator />

        <div className="flex min-w-0 flex-col gap-3 overflow-hidden">
          <div className="flex flex-wrap items-center gap-2">
            <ToggleGroup
              type="single"
              variant="outline"
              size="sm"
              value={scope}
              // A single-select ToggleGroup yields "" when the active item is clicked again, which would leave
              // no scope selected and nothing to query.
              onValueChange={(next) => {
                if (!next) return;
                setFellBack(false);
                setScope(next as RootScope);
              }}
            >
              {(Object.keys(ScopeLabels) as RootScope[]).map((value) => (
                <ToggleGroupItem key={value} value={value}>
                  {ScopeLabels[value]}
                </ToggleGroupItem>
              ))}
            </ToggleGroup>

            {attributionGap !== undefined && attributionGap !== 0 && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Badge variant="warning">
                    <AlertTriangle className="size-3" />
                    {attributionGap > 0
                      ? `${numberFormat.format(attributionGap)} not named`
                      : `${numberFormat.format(-attributionGap)} more than counted`}
                  </Badge>
                </TooltipTrigger>
                <TooltipContent className="max-w-sm">
                  {attributionGap > 0
                    ? `This cycle counted ${numberFormat.format(cycle.rootsUnreachable)} unreachable root${cycle.rootsUnreachable === 1 ? "" : "s"} but its client named only ${numberFormat.format(unreachable.length)}, so the rest cannot be listed.`
                    : `This cycle named ${numberFormat.format(unreachable.length)} unreachable roots but counted only ${numberFormat.format(cycle.rootsUnreachable)}.`}
                </TooltipContent>
              </Tooltip>
            )}
          </div>

          {fellBack && (
            <p className="text-xs text-muted-foreground">
              No roots are listed for this cycle: either it is older than the per-cycle list of roots, or its client
              counted roots it did not name. Showing the roots that are down now instead.
            </p>
          )}

          {rootsQuery.isFetching ? (
            <div className="flex items-center justify-center gap-2 py-12 text-sm text-muted-foreground">
              <Loader2 className="size-4 animate-spin" />
              Loading roots&hellip;
            </div>
          ) : rootsQuery.isError ? (
            <TableEmptyState
              icon={AlertTriangle}
              title="Could not load roots"
              description="The configured source roots could not be fetched. Try again in a moment."
            />
          ) : roots.length === 0 ? (
            <TableEmptyState
              icon={FolderX}
              title={
                scope === "cycle" ? "No roots listed for this cycle" : "No root is unreachable right now"
              }
              description={
                scope === "cycle"
                  ? "Either this cycle is older than the per-cycle list of roots, or its client does not send them. Switch to “Unreachable now” for the roots that are down today."
                  : "Every configured root was readable the last time a cycle reported it."
              }
            />
          ) : (
            <div className="min-w-0 overflow-auto">
              <ConfiguredSourceRootsTable roots={roots} />
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ConfiguredSourceRootsModal;
